import React from 'react';
import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import clsx from 'clsx';
import { Sidebar } from './components/Sidebar';
import { LoginForm } from './components/LoginForm';
import { Header } from './components/Header';
import { Dashboard } from './components/Dashboard';
import { Projects } from './components/Projects';
import { ProjectDetail } from './components/ProjectDetail';
import { Resources } from './components/Resources';
import { ResourceDetail } from './components/ResourceDetail';
import { Clients } from './components/Clients';
import { ClientDetail } from './components/ClientDetail';
import { Finance } from './components/Finance';
import { TimeTracking } from './components/TimeTracking';
import { Reports } from './components/Reports';
import { Documents } from './components/Documents';
import { XeroCallback } from './components/XeroCallback';
import { XeroProvider } from './contexts/XeroContext';
import { useAuth } from './hooks/useAuth';

export default function App() {
  const { isLoggedIn, user, login, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // Get the current path
  const currentPath = window.location.pathname;
  const isXeroCallback = currentPath.startsWith('/xero-callback');

  // If it's the Xero callback, handle it regardless of login state
  if (isXeroCallback) {
    return (
      <XeroProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/xero-callback" element={<XeroCallback />} />
            <Route path="/xero-callback/" element={<XeroCallback />} />
          </Routes>
        </BrowserRouter>
      </XeroProvider>
    );
  }

  // For all other routes, check login
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
        <LoginForm onLogin={login} />
      </div>
    );
  }

  return (
    <XeroProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-gray-950 flex relative">
          <Header isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
          <Sidebar isOpen={isSidebarOpen} onLogout={logout} />
          <main className={clsx(
            'flex-1 min-h-screen w-full',
            {'lg:pl-64': !isSidebarOpen}
          )}>
            <div className="pt-20 px-4 sm:px-6 lg:px-8 pb-8 min-w-0">
              <Routes>
                <Route path="/dashboard" element={<Dashboard username={user?.displayName || 'User'} role={user?.role as 'Executive' | 'Project Manager'} />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/:id" element={<ResourceDetail />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/:id" element={<ClientDetail />} />
                <Route path="/finance" element={<Finance />} />
                <Route path="/time-tracking" element={<TimeTracking />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </div>
          </main>
        </div>
      </BrowserRouter>
    </XeroProvider>
  );
}